import * as React from 'react';
import { useHoneycombTheme, Styler, Picture, Container, alpha, useBreakpoints, } from '@honeycomb/ui-core';
import { HeroContent } from './HeroContent';
import { HeroSearch } from './HeroSearch';
import { HeroOverlayImage } from './HeroOverlayImage';
export const heroTheme = {
    minHeight: {
        xs: 'auto',
        l: '565px',
        xxl: '640px',
    },
    paddingBottom: {
        xs: '0',
        l: '40px',
        xxl: '50px',
    },
    imageHeight: {
        xs: '90px',
        s: '140px',
        m: '180px',
        l: 'auto',
    },
    contentPaddingY: {
        xs: '24px',
        m: '40px',
    },
    contentPaddingRight: {
        l: '24px',
    },
    gradientColor: 'primary.tint.600',
    gradientIntensity: {
        none: 0,
        light: 0.6,
        dark: 1,
    },
};
export function Hero(props) {
    const { children, imageSet, ImageProps, panelColor: panelColorProp = 'primary.base', hideImageMobile: hideImageMobileProp = false, variant = 'panelled', textAlignment = 'start', gradient = 'none', } = props;
    const panelled = variant === 'panelled';
    const centered = !panelled && textAlignment === 'center';
    const hideImageMobile = hideImageMobileProp;
    const mobileImageAboveContent = panelled && !hideImageMobile;
    const hideImageMobileFullWidth = hideImageMobile && !panelled;
    const { largeUp } = useBreakpoints();
    const { theme } = useHoneycombTheme();
    const panelColor = theme.fn.resolveColorValue(panelColorProp);
    const gradientColor = alpha(theme.fn.getColor(heroTheme.gradientColor), heroTheme.gradientIntensity[gradient]);
    let contentChild;
    let searchChild;
    let overlayImageChild;
    React.Children.forEach(children, (element) => {
        switch (React.isValidElement(element) && element.type) {
            case HeroContent:
                contentChild = element;
                break;
            case HeroSearch:
                searchChild = element;
                break;
            case HeroOverlayImage:
                overlayImageChild = element;
                break;
            default:
                break;
        }
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Styler, { component: "section", "data-hero": true, pos: "relative", display: "flex", flexDirection: "column", mih: Object.assign({}, heroTheme.minHeight), pt: {
                xs: !mobileImageAboveContent ? 0 : heroTheme.imageHeight.xs,
                s: !mobileImageAboveContent ? 0 : heroTheme.imageHeight.s,
                m: !panelled ? 0 : heroTheme.imageHeight.m,
                l: 0,
            }, pb: Object.assign({}, heroTheme.paddingBottom) },
            React.createElement(Styler, Object.assign({ "data-hero-background": true, pos: "absolute", top: 0, left: 0, w: "100%", h: "100%", display: "flex", flexDirection: { xs: 'column', l: 'row-reverse' } }, (hideImageMobileFullWidth ? { bg: { xs: panelColor, m: 'none' } } : {})),
                React.createElement(Styler, { "data-hero-image": true, pos: "relative", display: {
                        xs: hideImageMobile ? 'none' : 'block',
                        s: hideImageMobile ? 'none' : 'block',
                        m: 'block',
                    }, flex: {
                        xs: `0 0 ${panelled ? heroTheme.imageHeight.xs : '100%'}`,
                        s: `0 0 ${panelled ? heroTheme.imageHeight.s : '100%'}`,
                        m: `0 0 ${panelled ? heroTheme.imageHeight.m : '100%'}`,
                        l: '1 1 0', // horizontal layout
                    } },
                    imageSet && (React.createElement(Picture, Object.assign({ imageSet: imageSet, pos: "absolute", top: 0, left: 0, w: "100%", h: "100%", objectFit: "cover" }, ImageProps))),
                    gradient !== 'none' && !panelled && (React.createElement(Styler, { "data-hero-gradient": true, pos: "absolute", top: 0, left: 0, w: "100%", h: "100%", bg: centered
                            ? `linear-gradient(180deg, ${gradientColor} 0%, transparent 50%)`
                            : `linear-gradient(90deg, ${gradientColor} 0%, transparent 50%)` }))),
                panelled && React.createElement(Styler, { "data-hero-panel": true, flex: "1 1 0", bg: panelColor })),
            (contentChild || overlayImageChild) && (React.createElement(Container, { "data-hero-content-container": true, flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "space-between", py: Object.assign({}, heroTheme.contentPaddingY) },
                overlayImageChild && centered && (React.createElement(Styler, { w: overlayImageChild.props.w, display: { xs: 'none', l: 'block' } })),
                contentChild && (React.createElement(Styler, { pr: !centered ? Object.assign({}, heroTheme.contentPaddingRight) : undefined, flexBasis: { xs: '100%', l: !centered ? '50%' : undefined }, ta: !panelled ? textAlignment : 'start' }, contentChild)),
                overlayImageChild && overlayImageChild)),
            largeUp && searchChild && (React.createElement(Container, { flexShrink: 0, disableContentMargins: { xs: true, l: false } }, searchChild))),
        !largeUp && searchChild));
}
